import * as RadioGroupPrimitive from "@radix-ui/react-radio-group"
import { cva } from "class-variance-authority"
import * as React from "react"

import { cn } from "../lib/utils"

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => (
  <RadioGroupPrimitive.Root ref={ref} className={cn("grid gap-2", className)} {...props} />
))
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName

const radioGroupItemVariants = cva(
  [
    "peer grid size-4 shrink-0 place-content-center rounded-full border border-border bg-bg-white transition-colors",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
    "disabled:cursor-not-allowed disabled:border-disabled disabled:bg-background",
  ],
  {
    variants: {
      variant: {
        primary: "focus-visible:ring-primary data-[state=checked]:border-primary",
        secondary: "focus-visible:ring-secondary data-[state=checked]:border-secondary",
      },
    },
    defaultVariants: {
      variant: "primary",
    },
  }
)

const radioGroupIndicatorVariants = cva("size-2 rounded-full", {
  variants: {
    variant: {
      primary: "bg-primary",
      secondary: "bg-secondary",
    },
  },
  defaultVariants: {
    variant: "primary",
  },
})

type RadioGroupItemProps = React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> & {
  variable?: "primary" | "secondary"
  variant?: "primary" | "secondary"
}

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioGroupItemProps
>(({ className, variable, variant = variable ?? "primary", ...props }, ref) => (
  <RadioGroupPrimitive.Item
    ref={ref}
    className={cn(radioGroupItemVariants({ variant }), className)}
    {...props}
  >
    <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
      <span className={radioGroupIndicatorVariants({ variant })} />
    </RadioGroupPrimitive.Indicator>
  </RadioGroupPrimitive.Item>
))
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName

export { RadioGroup, RadioGroupItem }
